import React, { useEffect, useState } from 'react';
import { getAdminQRCodes, generateQRCode, deleteQRCode, QRCodeEntry } from '../../../api/portalApi';

interface Props {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

const AdminQRSection: React.FC<Props> = ({ showToast }) => {
  const [codes, setCodes] = useState<QRCodeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [label, setLabel] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    getAdminQRCodes()
      .then((r) => setCodes(r.codes))
      .catch(() => showToast('Failed to load QR codes.', 'error'))
      .finally(() => setLoading(false));
  // showToast is stable — defined once in parent
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !targetUrl.trim()) return;
    setGenerating(true);
    try {
      const { code } = await generateQRCode(label.trim(), targetUrl.trim());
      setCodes((prev) => [code, ...prev]);
      setLabel('');
      setTargetUrl('');
      showToast('QR code generated and saved.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to generate QR code.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteQRCode(id);
      setCodes((prev) => prev.filter((c) => c._id !== id));
      showToast('QR code deleted.', 'success');
    } catch {
      showToast('Failed to delete.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: '1.2rem', fontWeight: 800 }}>QR Code Generator</h2>
      <p style={{ margin: '0 0 28px', color: '#64748b', fontSize: '0.85rem' }}>
        Generated codes are saved permanently on Cloudinary. 1200×1200 px — print-ready (300 dpi).
      </p>

      {/* Generator form */}
      <form
        onSubmit={handleGenerate}
        style={{
          background: '#1e293b',
          borderRadius: 14,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          marginBottom: 36,
          maxWidth: 560,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>
            Label
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Website, WhatsApp, Instagram..."
            required
            style={{
              padding: '11px 14px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.1)',
              background: '#0f172a',
              color: '#f1f5f9',
              fontSize: '0.95rem',
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>
            Target URL
          </label>
          <input
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://nomadme.life or https://wa.me/21622000000?text=..."
            required
            type="url"
            style={{
              padding: '11px 14px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.1)',
              background: '#0f172a',
              color: '#f1f5f9',
              fontSize: '0.95rem',
            }}
          />
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#475569' }}>
            WhatsApp tip: <code style={{ color: '#94a3b8' }}>https://wa.me/21622000000?text=Hello</code>
          </p>
        </div>

        <button
          type="submit"
          disabled={generating}
          style={{
            marginTop: 4,
            padding: '13px',
            borderRadius: 10,
            border: 'none',
            background: generating ? '#334155' : 'linear-gradient(90deg, #38bdf8, #818cf8)',
            color: generating ? '#64748b' : '#000',
            fontWeight: 800,
            fontSize: '0.9rem',
            cursor: generating ? 'wait' : 'pointer',
          }}
        >
          {generating ? '⏳ Generating & uploading...' : '⚡ Generate QR Code'}
        </button>
      </form>

      {/* Saved QR codes */}
      <h3 style={{ margin: '0 0 16px', fontSize: '0.9rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748b' }}>
        Saved Codes ({codes.length})
      </h3>

      {loading ? (
        <p style={{ color: '#475569' }}>Loading...</p>
      ) : codes.length === 0 ? (
        <p style={{ color: '#475569', fontSize: '0.88rem' }}>No QR codes yet. Generate your first one above.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
          {codes.map((code) => (
            <div
              key={code._id}
              style={{
                background: '#1e293b',
                borderRadius: 14,
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <img
                src={code.imageUrl}
                alt={code.label}
                style={{ width: 160, height: 160, borderRadius: 8, background: '#fff', padding: 4, boxSizing: 'border-box' }}
              />
              <div style={{ width: '100%', textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: '#f1f5f9' }}>{code.label}</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.68rem', color: '#475569', wordBreak: 'break-all' }}>
                  {code.targetUrl}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '0.65rem', color: '#334155' }}>
                  {new Date(code.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                <a
                  href={code.imageUrl}
                  download={`${code.label.replace(/\s+/g, '-').toLowerCase()}-qr.png`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '9px 0',
                    borderRadius: 8,
                    background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    textDecoration: 'none',
                  }}
                >
                  ⬇ Download
                </a>
                <button
                  type="button"
                  disabled={deletingId === code._id}
                  onClick={() => handleDelete(code._id)}
                  style={{
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1px solid rgba(239,68,68,0.3)',
                    background: 'transparent',
                    color: deletingId === code._id ? '#334155' : '#ef4444',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    cursor: deletingId === code._id ? 'wait' : 'pointer',
                  }}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminQRSection;
